import { CSSProperties, memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { RgbaColor, RgbaColorPicker } from 'react-colorful';
import chroma from 'chroma-js';
import { debounce } from 'underscore';

import { ColorPickerProps, OnColorPickerChangeResult } from './color-picker.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import HexInput from './hex-input';
import RGBAInputs from './rgba-inputs';
import EyeDropper from './eye-dropper/eye-dropper';
import PresetColors from './preset-colors';

import './color-picker.css';

const DEFAULT_COLOR: RgbaColor = { r: 50, g: 100, b: 150, a: 1 };

const toRgba = (color: string): RgbaColor => {
  const [r, g, b, a] = chroma(color).rgba();
  return { r, g, b, a };
};

const toRgbaString = ({ r, g, b, a }: RgbaColor) => `rgba(${r}, ${g}, ${b}, ${a})`;

// two colors are the same when they match channel for channel, alpha included - however they're written
const sameColor = (first: RgbaColor | string, second: RgbaColor | string) => {
  const asString = (color: RgbaColor | string) => (typeof color === 'string' ? color : toRgbaString(color));
  return chroma(asString(first)).hex('rgba') === chroma(asString(second)).hex('rgba');
};

const toResult = (color: RgbaColor): OnColorPickerChangeResult => {
  const rgba = toRgbaString(color);
  return { rgba, hex: chroma(rgba).hex(), rgb: `rgb(${color.r}, ${color.g}, ${color.b})`, opacity: color.a };
};

const ColorPicker = memo(
  ({
    initialColor,
    value,
    onChange,
    containerClassName,
    pickerClassName,
    style,
    presetColors,
    presetColorsLabel,
    saturationPickerHeight,
    width,
    boxShadow,
  }: ColorPickerProps) => {
    const id = useId();

    // the one color everything in the picker shows - the hex and RGBA fields only keep their own text
    // while they're being typed in
    const [color, setColor] = useState<RgbaColor>(() => {
      const start = value ?? initialColor;
      return start && chroma.valid(start) ? toRgba(start) : DEFAULT_COLOR;
    });

    const colorRef = useRef(color);
    useEffect(() => {
      colorRef.current = color;
    }, [color]);

    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    // the last color sent to the parent, so a controlled value coming back from it isn't mistaken for a
    // change made outside the picker
    const lastEmittedRef = useRef<string | null>(null);
    const emitChange = useRef(
      debounce((next: RgbaColor) => {
        const result = toResult(next);
        lastEmittedRef.current = result.rgba;
        onChangeRef.current?.(result);
      }, 100)
    ).current;

    // every change made in the picker goes through here
    const applyColor = useCallback(
      (next: RgbaColor) => {
        setColor(next);
        emitChange(next);
      },
      [emitChange]
    );

    // a controlled value only replaces the color when it was changed outside the picker - e.g. a reset.
    // Our own changes come back as value too, and taking those would overwrite whatever's been done since
    useEffect(() => {
      if (value === undefined) return;
      const next = value && chroma.valid(value) ? toRgba(value) : DEFAULT_COLOR;
      if (sameColor(next, colorRef.current)) return;
      if (lastEmittedRef.current && sameColor(next, lastEmittedRef.current)) return;
      setColor(next);
    }, [value]);

    // react-colorful's picker can call onChange on its own when its color changes, with the previous color
    // rounded through HSV - so it's only listened to while it's actually being dragged or keyed
    const interactingRef = useRef(false);
    const stopInteracting = useCallback(() => {
      // a couple of frames, so the drag's last move still lands before it's no longer listened to
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          interactingRef.current = false;
        })
      );
    }, []);
    useEffect(() => {
      window.addEventListener('pointerup', stopInteracting);
      window.addEventListener('pointercancel', stopInteracting);
      return () => {
        window.removeEventListener('pointerup', stopInteracting);
        window.removeEventListener('pointercancel', stopInteracting);
      };
    }, [stopInteracting]);

    const handlePickerChange = useCallback(
      (next: RgbaColor) => {
        if (interactingRef.current) applyColor(next);
      },
      [applyColor]
    );

    // the hex field has no alpha, so typing a hex keeps the current one
    const handleHexChange = useCallback(
      (hex: string) => applyColor({ ...toRgba(hex), a: colorRef.current.a }),
      [applyColor]
    );

    const handleCustomSelectColor = useCallback((selected: string) => applyColor(toRgba(selected)), [applyColor]);

    const eyeDropperAvailable = typeof window !== 'undefined' && !!window?.EyeDropper;
    const hexId = `${id}-hex`;

    return (
      <div
        className={`color-picker-container ${containerClassName || ''}`}
        style={
          {
            ...(saturationPickerHeight ? { '--saturationPickerHeight': saturationPickerHeight } : {}),
            ...(width ? { '--colorPickerWidth': width } : {}),
            ...(boxShadow ? { '--colorPickerBoxShadow': boxShadow } : {}),
          } as CSSProperties
        }
      >
        <div className="rgba-picker-container">
          <RgbaColorPicker
            color={color}
            onChange={handlePickerChange}
            onPointerDown={() => {
              interactingRef.current = true;
            }}
            onKeyDown={() => {
              interactingRef.current = true;
            }}
            onKeyUp={stopInteracting}
            className={`color-picker ${pickerClassName || ''} ${eyeDropperAvailable ? 'eye-dropper-available' : ''}`}
            style={style || {}}
          />

          <OptionalComponent display={eyeDropperAvailable}>
            <div className="color-picker-eye-dropper-container">
              <EyeDropper onSelectColor={handleCustomSelectColor} />
            </div>
          </OptionalComponent>

          <div
            style={{ backgroundColor: toRgbaString(color) }}
            className="square-32 radius-4 color-picker-display-block"
          />
        </div>

        <div className="flex fd-row jc-center ai-center color-picker-inputs-container">
          <div className="flex-column jc-center ai-center hex-color-input-wrapper flex-1">
            <HexInput
              id={hexId}
              className="text-center radius-100 clear-input"
              value={chroma(toRgbaString(color)).hex('rgb').slice(1).toUpperCase()}
              onChange={handleHexChange}
            />
            <label className="width-100-percent text-center" htmlFor={hexId}>
              HEX
            </label>
          </div>

          <RGBAInputs idPrefix={`${id}-rgba`} color={color} onChange={applyColor} />
        </div>

        <PresetColors colors={presetColors || []} onSelectColor={handleCustomSelectColor} label={presetColorsLabel} />
      </div>
    );
  }
);

export default ColorPicker;
